/**
 * clientOnlyMessageFields — the ChatMessage slots that must never reach a
 * persist boundary (2026-09-08, PR-2).
 *
 *   attachments      live `File[]` — not JSON-roundtrippable (the 2026-05-11
 *                    thumbnails regression); `attachmentsSerialized` is the record
 *   deliveryStatus   render-only annotation derived from the delivery ledger
 *   deliveryFailure  render-only reason text for a failed send
 *
 * Applied by BOTH boundaries — `sanitizeMessageForPersist` (localStorage /
 * the thread store) and `toMessageDoc` (Firestore) — so a status can never be
 * frozen into a stored message and read back as truth on another device.
 * `sharedMessageId` is NOT here: it is the durable join key and must persist.
 */
export const CLIENT_ONLY_MESSAGE_FIELDS = ['attachments', 'deliveryStatus', 'deliveryFailure'] as const;

export type ClientOnlyMessageField = (typeof CLIENT_ONLY_MESSAGE_FIELDS)[number];

/**
 * Returns the SAME reference when nothing had to be stripped (callers depend on
 * identity for React diffing), else a shallow copy without the client-only slots.
 */
export function stripClientOnlyMessageFields<T extends object>(message: T): T {
  if (!message || typeof message !== 'object') return message;
  const rec = message as Record<string, unknown>;
  let dirty = false;
  for (const f of CLIENT_ONLY_MESSAGE_FIELDS) {
    if (rec[f] !== undefined) { dirty = true; break; }
  }
  if (!dirty) return message;
  const copy: Record<string, unknown> = { ...rec };
  for (const f of CLIENT_ONLY_MESSAGE_FIELDS) delete copy[f];
  return copy as T;
}
